import { useRef, useState, useEffect } from 'react';
import {
    Button,
    Spinner,
    Badge
} from 'reactstrap';
import { FaSearch, FaEdit, FaTrash, FaPlus, FaEye, FaCheck, FaFileImport } from 'react-icons/fa';
import { CLIENT_FORM_PAGE } from '../../routes/routeConstants';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { clientActions } from '../../sagas/clientSlice';
import Swal from 'sweetalert2';
import Pagination from '../Common/Pagination';
import './ClientCompact.css';

const SEARCH_DEBOUNCE_MS = 300;

const ClientList = () => {

    const navigate = useNavigate();
    const location = useLocation();

    const getClientFormPath = () => {
        if (location.pathname.startsWith('/sales-module')) {
            return '/sales-module/clients/new';
        }
        return CLIENT_FORM_PAGE;
    };

    const getClientEditPath = (client) => {
        if (location.pathname.startsWith('/sales-module')) {
            return `/sales-module/clients/${client.id}/edit`;
        }
        return CLIENT_FORM_PAGE;
    };

    // États
    const { clients, loading, error, totalElements, totalPages, pageSize } = useSelector(state => state.clients);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(0);
    const fileInputRef = useRef(null);
    const dispatch = useDispatch();

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
            setCurrentPage(0);
        }, SEARCH_DEBOUNCE_MS);

        return () => clearTimeout(timeoutId);
    }, [searchTerm]);

    useEffect(() => {
        dispatch(clientActions.findAll({
            withSituation: false,
            query: debouncedSearchTerm,
            page: currentPage,
            size: pageSize
        }));// Appel de l'action pour récupérer les clients    
    }, [currentPage, debouncedSearchTerm, dispatch, pageSize]);

    const handleSearchChange = (value) => {
        setSearchTerm(value);
    };


    const handleDeleteClient = (clientId) => {
        Swal.fire({
            title: 'Êtes-vous sûr ?',
            text: "Le client sera désactivé et ne pourra plus être utilisé.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Oui, désactiver !',
            cancelButtonText: 'Annuler'
        }).then((result) => {
            if (result.isConfirmed) {
                let payload = {
                    id: clientId,
                    onSuccess: () => {
                        Swal.fire(
                            'Inactivé !',
                            'Le client a été désactivé avec succès.',
                            'success'
                        );
                        dispatch(clientActions.findAll({
                            withSituation: false,
                            query: searchTerm,
                            page: currentPage,
                            size: pageSize
                        })); // Recharger la liste des clients après la suppression
                    },
                    onError: () => {
                        Swal.fire(
                            'Erreur !',
                            'Une erreur s\'est produite lors de la désactivation.',
                            'error'
                        );
                    }
                }

                dispatch(clientActions.inactivate(payload));
            }
        });
    };

    const handleActiverClient = (clientId) => {
        Swal.fire({
            title: 'Êtes-vous sûr ?',
            text: "Le client sera activé et pourra être utilisé.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Oui, activer !',
            cancelButtonText: 'Annuler'
        }).then((result) => {
            if (result.isConfirmed) {
                let payload = {
                    id: clientId,
                    onSuccess: () => {
                        Swal.fire(
                            'Activé !',
                            'Le client a été activé avec succès.',
                            'success'
                        );
                        dispatch(clientActions.findAll({
                            withSituation: false,
                            query: searchTerm,
                            page: currentPage,
                            size: pageSize
                        })); // Recharger la liste des clients après la suppression
                    },
                    onError: () => {
                        Swal.fire(
                            'Erreur !',
                            'Une erreur s\'est produite lors de l\'activation.',
                            'error'
                        );
                    }
                }
                dispatch(clientActions.activate(payload));
            }
        });
    };

    const handleEdit = (client) => {
        const path = getClientEditPath(client);
        navigate(path, { state: { client } });
    };

    const handleImportClick = () => {
        fileInputRef.current?.click();
    };

    const handleImportFile = (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';

        if (!file) return;

        const extension = file.name.split('.').pop()?.toLowerCase();
        if (!['xlsx', 'xls'].includes(extension)) {
            Swal.fire('Format invalide', 'Veuillez importer un fichier Excel .xlsx ou .xls avec les colonnes name et type.', 'warning');
            return;
        }

        dispatch(clientActions.importClients({
            file,
            onSuccess: (result) => {
                const imported = result?.imported || 0;
                const skipped = result?.skipped || 0;
                const errors = result?.errors || [];

                Swal.fire({
                    icon: imported > 0 ? 'success' : 'warning',
                    title: 'Import clients termine',
                    text: `${imported} client(s) importé(s), ${skipped} ligne(s) ignorée(s). ${errors.slice(0, 5).join(' — ')}`
                });

                setCurrentPage(0);
                dispatch(clientActions.findAll({ withSituation: false, query: searchTerm, page: 0, size: pageSize }));
            },
            onError: () => {
                Swal.fire('Erreur', "Impossible d'importer le fichier. Verifiez les colonnes name et type.", 'error');
            }
        }));
    };

    return (
        <section className="client-compact">
            <header className="client-compact-header">
                <h4>Clients <small className="text-muted">({totalElements || 0})</small></h4>
                <div className="client-compact-actions">
                    <input type="file" ref={fileInputRef} accept=".xlsx,.xls" className="d-none" onChange={handleImportFile} />
                    <Button color="secondary" outline size="sm" onClick={handleImportClick} disabled={loading}><FaFileImport aria-hidden="true" /> Importer Excel</Button>
                    <Button color="primary" size="sm" onClick={() => navigate(getClientFormPath())}><FaPlus aria-hidden="true" /> Nouveau client</Button>
                </div>
            </header>
            <div className="client-compact-panel">
                <div className="input-group client-compact-search">
                    <span className="input-group-text"><FaSearch aria-hidden="true" /></span>
                    <input type="search" className="form-control" aria-label="Rechercher un client" placeholder="Nom, ICE / CIN ou téléphone" value={searchTerm} onChange={event => handleSearchChange(event.target.value)} />
                </div>
                {error && <div role="alert" className="alert alert-danger py-2">Impossible de charger les clients. <Button size="sm" color="link" onClick={() => dispatch(clientActions.findAll({ query: debouncedSearchTerm, page: currentPage, size: pageSize }))}>Réessayer</Button></div>}
                {loading ? <div className="client-compact-loading"><Spinner size="sm" /> Chargement…</div> : !error && <>
                    <div className="table-responsive">
                        <table className="table table-hover client-compact-table">
                            <thead><tr><th scope="col">Nom</th><th scope="col">ICE / CIN</th><th scope="col">Téléphone</th><th scope="col" className="text-end">Actions</th></tr></thead>
                            <tbody>
                                {clients.map(client => (
                                    <tr key={client.id}>
                                        <td><strong>{(client.typeClient === 'ENTREPRISE' ? client.entreprise : [client.nom, client.prenom].filter(Boolean).join(' ')) || client.nom || client.entreprise || '—'}</strong>{!client.active && <Badge color="secondary" className="ms-2">Inactif</Badge>}</td>
                                        <td>{(client.typeClient === 'ENTREPRISE' ? client.ice || client.cin : client.cin || client.ice) || '—'}</td>
                                        <td>{client.telephone1 || '—'}</td>
                                        <td><div className="client-compact-row-actions">
                                            <Button color="secondary" outline size="sm" title="Situation du client" aria-label="Situation du client" onClick={() => navigate(`/sales-module/customers?clientId=${client.id}`)}><FaEye aria-hidden="true" /></Button>
                                            <Button color="primary" outline size="sm" onClick={() => handleEdit(client)}><FaEdit aria-hidden="true" /> Modifier</Button>
                                            <Button color={client.active ? 'danger' : 'success'} outline size="sm" title={client.active ? 'Désactiver le client' : 'Activer le client'} aria-label={client.active ? 'Désactiver le client' : 'Activer le client'} onClick={() => client.active ? handleDeleteClient(client.id) : handleActiverClient(client.id)}>{client.active ? <FaTrash aria-hidden="true" /> : <FaCheck aria-hidden="true" />}</Button>
                                        </div></td>
                                    </tr>
                                ))}
                                {!clients.length && <tr><td colSpan="4" className="text-center text-muted py-3">{searchTerm ? 'Aucun client trouvé.' : 'Aucun client. Cliquez sur « Nouveau client » pour commencer.'}</td></tr>}
                            </tbody>
                        </table>
                    </div>
                    <footer className="client-compact-list-footer">
                        <small className="text-muted">{totalElements > 0 ? `${currentPage * pageSize + 1}–${Math.min((currentPage + 1) * pageSize, totalElements)} sur ${totalElements}` : '0 client'}</small>
                        <Pagination totalPages={totalPages || 0} currentPage={currentPage} onPageChange={setCurrentPage} />
                    </footer>
                </>}
            </div>
        </section>
    );
};

export default ClientList;
